import { useState, useMemo, useEffect } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useAuthStore } from '@/stores/authStore'
import { Settings as SettingsIcon } from 'lucide-react'
import type { Role } from '@/types'
import {
  LeadSourceTab,
  SecurityTab,
  RecycleBinTab,
  AccountTab,
  UsersTab,
  EsforcoTimeTab,
  FluxosTab,
  AuditLogTab,
  BackupTab,
  SystemLogsTab,
  ConfigTabsNav,
  AppearanceTab,
  FinanceiroWrapperTab,
  GestaoTab,
} from '@/components/Configuracao'
import { getVisibleConfigurationTabs, type ConfigTabId } from '@/components/Configuracao/configuracaoTabs'
import { EmailTemplatesTab } from '@/components/Email'
import { useConfiguracaoTheme } from '@/hooks/useConfiguracaoTheme'

export default function Configuracao() {
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState<ConfigTabId>('account')
  const themeActions = useConfiguracaoTheme()

  const visibleTabs = useMemo(() => {
    const userRole = (user?.role || 'bdr').toLowerCase() as Role
    return getVisibleConfigurationTabs(userRole, undefined, { email: user?.email })
  }, [user?.role, user?.email])

  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0].id)
    }
  }, [visibleTabs, activeTab])

  return (
    <DashboardLayout>
      <div className="flex items-center gap-3">
        <div className="w-1.5 h-6 bg-accent rounded-full" />
        <div>
          <h1 className="text-lg font-black text-text-primary uppercase tracking-[2px] flex items-center gap-2">
            <SettingsIcon size={18} className="text-accent" />
            Configurações
          </h1>
          <p className="text-xs text-text-muted">Gerencie usuários, permissões e integrações do sistema</p>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <ConfigTabsNav
          tabs={visibleTabs}
          activeTab={activeTab}
          onTabChange={(id) => setActiveTab(id as ConfigTabId)}
        />

        <div className="min-w-0">
          {activeTab === 'account' && <AccountTab user={user} />}
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'gestao' && <GestaoTab />}
          {activeTab === 'esforco' && <EsforcoTimeTab />}
          {activeTab === 'sources' && <LeadSourceTab />}
          {activeTab === 'finance' && <FinanceiroWrapperTab />}
          {activeTab === 'email' && <EmailTemplatesTab />}
          {activeTab === 'security' && <SecurityTab />}
          {activeTab === 'recycle' && <RecycleBinTab />}
          {activeTab === 'fluxos' && <FluxosTab />}
          {activeTab === 'appearance' && <AppearanceTab {...themeActions} />}
          {activeTab === 'audit' && <AuditLogTab />}
          {activeTab === 'logs' && <SystemLogsTab />}
          {activeTab === 'backup' && <BackupTab />}
        </div>
      </div>
    </DashboardLayout>
  )
}
